import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { createUserProfile, updateUserProfile } from '@/lib/data-access/user-profile';
import { useToast } from '@/hooks/use-toast';
import type { Branch, Status } from '@/types/database';

const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(100),
  last_name: z.string().min(1, 'Last name is required').max(100),
  email: z.string().email('Invalid email address').max(255),
  branch: z.enum(['Army', 'Navy', 'Marine Corps', 'Air Force', 'Space Force', 'Coast Guard', 'Other']),
  status: z.enum(['ActiveDuty', 'GuardReserve', 'Veteran']),
  mos_code: z.string().min(1, 'MOS/Rate/AFSC is required').max(20),
  rank: z.string().min(1, 'Rank is required').max(20),
  years_of_service: z.coerce.number().min(0, 'Years must be 0 or more').max(50),
  separation_date: z.date().optional().nullable(),
  coding_background: z.string().max(500).optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export default function OnboardingProfile() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      branch: 'Army',
      status: 'Veteran',
      mos_code: '',
      rank: '',
      years_of_service: 0,
      separation_date: null,
      coding_background: '',
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    setIsSubmitting(true);
    try {
      // Get current user ID (this assumes you have auth set up)
      // For Appwrite, you'd get this from your auth context
      const userId = 'current-user-id'; // TODO: Replace with actual user ID from auth

      await createUserProfile({
        id: userId,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        branch: data.branch as Branch,
        status: data.status as Status,
        mos_code: data.mos_code,
        rank: data.rank,
        years_of_service: data.years_of_service,
        separation_date: data.separation_date ? data.separation_date.toISOString().split('T')[0] : null,
        coding_background: data.coding_background || null,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      toast({
        title: 'Profile saved',
        description: 'Your service background has been recorded.',
      });

      navigate('/onboarding/track');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-primary">Welcome to VeteranOS</CardTitle>
          <CardDescription>
            Let's start by capturing your service background. This helps us tailor your transition plan.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Name Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Email */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john.doe@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Service Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="branch"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Branch</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select branch" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Army">Army</SelectItem>
                          <SelectItem value="Navy">Navy</SelectItem>
                          <SelectItem value="Marine Corps">Marine Corps</SelectItem>
                          <SelectItem value="Air Force">Air Force</SelectItem>
                          <SelectItem value="Space Force">Space Force</SelectItem>
                          <SelectItem value="Coast Guard">Coast Guard</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="ActiveDuty">Active Duty</SelectItem>
                          <SelectItem value="GuardReserve">Guard/Reserve</SelectItem>
                          <SelectItem value="Veteran">Veteran</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="mos_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>MOS/Rate/AFSC</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., 11B, IT, 3E5X1" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rank"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rank</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., E-5, O-3" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="years_of_service"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Years of Service</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" max="50" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="separation_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Separation Date (Optional)</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full pl-3 text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value || undefined}
                            onSelect={field.onChange}
                            disabled={(date) => date < new Date()}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="coding_background"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Coding Background (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Completed CS50, built a personal website"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save & Continue'
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
